import { RotateCcw } from 'lucide-react';
import { cn } from '../../lib/cn';
import { CATEGORY_LABEL, FINDING_STATUS_LABEL, SEVERITY_LABEL } from '../../lib/format';
import type { FindingFilterState } from '../../lib/findingFilters';
import type { Category, FindingStatus, Severity } from '../../types/api';
import { CATEGORIES, FINDING_STATUSES, SEVERITIES } from '../../types/api';
import { SEV_DOT } from '../ui/Badges';

interface Props {
  value: FindingFilterState;
  onChange: (v: FindingFilterState) => void;
  files: string[];
  severityCounts: Record<Severity, number>;
  onReset: () => void;
}

export function FindingFilters({ value, onChange, files, severityCounts, onReset }: Props) {
  const toggleSeverity = (s: Severity) =>
    onChange({
      ...value,
      severities: value.severities.includes(s) ? value.severities.filter((x) => x !== s) : [...value.severities, s],
    });

  return (
    <div className="space-y-3">
      <div role="group" aria-label="Filter by severity" className="flex flex-wrap gap-2">
        {SEVERITIES.map((s) => {
          const on = value.severities.includes(s);
          return (
            <button
              key={s}
              type="button"
              aria-pressed={on}
              onClick={() => toggleSeverity(s)}
              className={cn(
                'inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-colors',
                on ? 'border-line bg-raised text-ink' : 'border-dashed border-line text-faint hover:text-muted',
              )}
            >
              <span className={cn('h-2 w-2 rounded-full', on ? SEV_DOT[s] : 'bg-line')} aria-hidden />
              {SEVERITY_LABEL[s]}
              <span className="tabular-nums text-faint">{severityCounts[s]}</span>
            </button>
          );
        })}
      </div>

      <div className="grid gap-2 sm:grid-cols-[1fr_1fr_1fr_auto]">
        <label className="sr-only" htmlFor="filter-category">
          Category
        </label>
        <select
          id="filter-category"
          className="input py-1.5"
          value={value.category}
          onChange={(e) => onChange({ ...value, category: e.target.value as Category | 'all' })}
        >
          <option value="all">All categories</option>
          {CATEGORIES.map((c) => (
            <option key={c} value={c}>
              {CATEGORY_LABEL[c]}
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor="filter-file">
          File
        </label>
        <select id="filter-file" className="input py-1.5" value={value.file} onChange={(e) => onChange({ ...value, file: e.target.value })}>
          <option value="all">All files</option>
          {files.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
        </select>

        <label className="sr-only" htmlFor="filter-status">
          Status
        </label>
        <select
          id="filter-status"
          className="input py-1.5"
          value={value.status}
          onChange={(e) => onChange({ ...value, status: e.target.value as FindingFilterState['status'] })}
        >
          <option value="all">All statuses</option>
          <option value="active">Not dismissed</option>
          {FINDING_STATUSES.map((s: FindingStatus) => (
            <option key={s} value={s}>
              {FINDING_STATUS_LABEL[s]}
            </option>
          ))}
        </select>

        <button
          type="button"
          onClick={onReset}
          className="inline-flex items-center justify-center gap-1.5 rounded-md px-3 text-xs text-muted hover:bg-raised hover:text-ink"
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden />
          Reset
        </button>
      </div>
    </div>
  );
}
